import React, { useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert, Button, IconButton, Tooltip } from '@mui/material';
import {
  IconReceipt,
  IconChevronRight,
  IconRefresh,
  IconExternalLink,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { InvoiceScheduleListItem, InvoiceScheduleDetail } from '@techgitter/contracts';
import { CARD_SX, ACCENT_LINK_SX } from '../../styles/designSystem';
import { Chip } from '../chip';
import { useCustomerInvoiceSchedules } from '../../hooks/useCustomerInvoiceSchedules';
import { getInvoiceSchedule } from '../../services/invoiceScheduleAdminService';

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

interface CustomerInvoiceSchedulesPanelProps {
  dataverseCustomerId: string;
  /** When true, show the "Manage in Admin" link (admins only). */
  isAdmin?: boolean;
}

/**
 * Read-only "Scheduled Invoices" card for the customer detail page. Lists the
 * customer's recurring invoice schedules; each row expands to lazily load and
 * show its line items. Create/edit/delete live on the Admin Console Invoices
 * tab, reached via the admin-only "Manage in Admin" link. (sm4rt-web-ui §4/§5.)
 */
export const CustomerInvoiceSchedulesPanel: React.FC<CustomerInvoiceSchedulesPanelProps> = ({
  dataverseCustomerId,
  isAdmin = false,
}) => {
  const navigate = useNavigate();
  const { schedules, isLoading, error, refresh } = useCustomerInvoiceSchedules(dataverseCustomerId);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, InvoiceScheduleDetail>>({});
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<Record<string, string>>({});

  const toggleExpand = useCallback(async (item: InvoiceScheduleListItem) => {
    const id = item.scheduleId;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!details[id]) {
      setDetailLoadingId(id);
      setDetailError((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      try {
        const detail = await getInvoiceSchedule(id);
        setDetails((prev) => ({ ...prev, [id]: detail }));
      } catch (err) {
        setDetailError((prev) => ({
          ...prev,
          [id]: err instanceof Error ? err.message : 'Failed to load line items',
        }));
      } finally {
        setDetailLoadingId((cur) => (cur === id ? null : cur));
      }
    }
  }, [expandedId, details]);

  return (
    <Box sx={{ ...CARD_SX, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <IconReceipt size={16} stroke={1.5} color="#4A90D9" />
        <div className="customer-detail__card-title" style={{ marginBottom: 0 }}>
          Scheduled Invoices
        </div>
        {!isLoading && schedules.length > 0 && (
          <Typography sx={{ fontSize: 12, color: '#888' }}>{schedules.length}</Typography>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} sx={{ color: '#aaa' }}>
              <IconRefresh size={16} stroke={1.5} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : schedules.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: '#888' }}>
          No scheduled invoices for this customer.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {schedules.map((item) => {
            const isExpanded = expandedId === item.scheduleId;
            const detail = details[item.scheduleId];
            const isDetailLoading = detailLoadingId === item.scheduleId;
            const itemDetailError = detailError[item.scheduleId];
            return (
              <Box
                key={item.scheduleId}
                sx={{
                  border: '1px solid #e6edf5',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpand(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(item);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f6f9fd' },
                  }}
                >
                  <IconChevronRight
                    size={16}
                    stroke={1.5}
                    color="#9aa7b5"
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.15s',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#1a1a1a',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#888' }}>
                      Next run: {item.nextRun || '—'} · {item.lineItemCount} item
                      {item.lineItemCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Chip tone="info">{item.frequency}</Chip>
                  {item.paused && <Chip tone="warning">Paused</Chip>}
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', flexShrink: 0 }}
                  >
                    {formatCurrency(item.subtotal)}
                  </Typography>
                </Box>

                {isExpanded && (
                  <Box sx={{ borderTop: '1px solid #eef2f7', px: 1.5, py: 1, bgcolor: '#fafcfe' }}>
                    {isDetailLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : itemDetailError ? (
                      <Alert severity="error" sx={{ py: 0 }}>
                        {itemDetailError}
                      </Alert>
                    ) : detail && detail.lineItems.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {detail.lineItems.map((line) => (
                          <Box
                            key={line.itemId}
                            sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                          >
                            <Typography sx={{ fontSize: 13, color: '#444', flex: 1, minWidth: 0 }}>
                              <Box component="span" sx={{ color: '#888' }}>
                                {line.quantity} ×{' '}
                              </Box>
                              {line.name}
                              {line.description ? (
                                <Box component="span" sx={{ color: '#aaa' }}>
                                  {' '}
                                  — {line.description}
                                </Box>
                              ) : null}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: '#444', flexShrink: 0 }}>
                              {formatCurrency(line.quantity * line.priceRetail)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 13, color: '#888' }}>
                        No line items on this schedule.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {isAdmin && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            endIcon={<IconExternalLink size={15} stroke={1.5} />}
            onClick={() => navigate('/admin/invoices')}
            sx={{ ...ACCENT_LINK_SX, fontSize: 13, textTransform: 'none' }}
          >
            Manage in Admin
          </Button>
        </Box>
      )}
    </Box>
  );
};
